<template>
  <!--
    Free Tier Partner Agreement — one-time popup overlay.

    Mounted globally in Web.vue + Chat.vue. The parent layout decides
    whether to render this component (gated on user_role === 'owner',
    tenant.pricing_mode === 'free', and free_terms_accepted_version
    drift). Once the box is checked and Agree clicked, the backend
    records acceptance on the global users/{uid} doc and we update the
    in-memory store so the modal does not re-trigger.

    By design the modal cannot be dismissed by clicking the backdrop or
    pressing Escape — accepting the partnership agreement is required.
  -->
  <div
    class="ftam-overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="ftam-heading"
    @mousedown.self.prevent
    @click.self.prevent
  >
    <div class="ftam-card" ref="card">
      <header class="ftam-header">
        <h2 id="ftam-heading" class="ftam-heading">
          Welcome to the Kiey Free Tier
        </h2>
        <p class="ftam-subhead">
          We've grown — please take a moment to read our updated Free Tier
          Partner Agreement.
        </p>
      </header>

      <section class="ftam-terms-wrap">
        <div
          v-if="terms_loading"
          class="ftam-terms ftam-terms-status"
          aria-busy="true"
        >
          Loading…
        </div>
        <div
          v-else-if="terms_error"
          class="ftam-terms ftam-terms-status ftam-terms-error"
        >
          {{ terms_error }}
        </div>
        <article
          v-else
          class="ftam-terms"
          tabindex="0"
          v-html="terms_html"
          aria-label="Free Tier Partner Agreement text"
        ></article>
      </section>

      <label class="ftam-checkbox-row">
        <input
          ref="checkbox"
          type="checkbox"
          v-model="agreement_checked"
          :disabled="submitting"
        />
        <span class="ftam-checkbox-label">
          I have read and agree to the Kiey Free Tier Partner Agreement
          and the spirit of the partnership it describes.
        </span>
      </label>

      <p v-if="submit_error" class="ftam-submit-error" role="alert">
        {{ submit_error }}
      </p>

      <div class="ftam-actions">
        <button
          ref="agree_btn"
          type="button"
          class="ftam-agree-btn"
          :disabled="!can_submit"
          @click="acceptAgreement"
        >
          {{ submitting ? 'Recording acceptance…' : 'I Agree — Continue' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config/constants.js';
import { renderLegalMarkdown } from '@/util/legal_md.js';

export default {
  name: 'FreeTierAgreementModal',
  data() {
    return {
      // Markdown fetch state. Lazy — populated on the first mount.
      terms_html: '',
      terms_loading: false,
      terms_error: '',
      // Required-checkbox state. Continue button is disabled until true.
      agreement_checked: false,
      // Spinner state while the PUT is in flight.
      submitting: false,
      // Surfaced inline if the PUT fails. Button stays clickable so the
      // user can retry without reloading the page.
      submit_error: '',
      // Snapshot of the focused element when the modal opened so we can
      // return focus on close (WCAG 2.4.3).
      previously_focused_el: null,
    };
  },
  // Non-reactive handler references — assigned in mounted(), detached in
  // beforeDestroy(). Held on `this` directly (not in data()) so Vue does
  // not waste a watcher on them and they don't trip the reserved-keys
  // lint rule.
  created() {
    this.focusTrapHandler = null;
    this.keyHandler = null;
  },
  computed: {
    can_submit() {
      return (
        this.agreement_checked &&
        !this.submitting &&
        !this.terms_loading &&
        !this.terms_error
      );
    },
  },
  mounted() {
    this.previously_focused_el =
      typeof document !== 'undefined' ? document.activeElement : null;

    this.loadTerms();
    this.installFocusTrap();
    this.installKeyHandler();
    // Move focus into the modal so screen readers announce it and
    // keyboard users start inside the dialog.
    this.$nextTick(() => {
      const card = this.$refs.card;
      if (card && typeof card.focus === 'function') {
        // The card itself isn't focusable; focus the first focusable
        // descendant we ship — the scrollable terms region. That keeps
        // focus inside the dialog without trapping it on the disabled
        // Agree button.
        const terms = card.querySelector('.ftam-terms');
        if (terms && typeof terms.focus === 'function') {
          terms.focus();
          return;
        }
        if (this.$refs.checkbox && typeof this.$refs.checkbox.focus === 'function') {
          this.$refs.checkbox.focus();
        }
      }
    });
  },
  beforeDestroy() {
    if (this.focusTrapHandler) {
      document.removeEventListener('focusin', this.focusTrapHandler, true);
      this.focusTrapHandler = null;
    }
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler, true);
      this.keyHandler = null;
    }
    // Restore focus to wherever the user was when the modal opened —
    // skipped if the previously-focused element is gone (route change,
    // logout, etc.).
    const prev = this.previously_focused_el;
    if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
      try { prev.focus(); } catch (_e) { /* best-effort */ }
    }
  },
  methods: {
    // Lazy-load the partner-agreement markdown on first mount and cache
    // the rendered HTML on the component. We deliberately use the same
    // /legal/free_tier_partner_agreement.md asset and renderLegalMarkdown
    // util used by the inline acceptance on /sign_up/billing — keeps a
    // single canonical text + a single rendering codepath.
    loadTerms() {
      this.terms_loading = true;
      this.terms_error = '';
      fetch('/legal/free_tier_partner_agreement.md', { cache: 'no-store' })
        .then((res) => {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then((text) => {
          this.terms_html = renderLegalMarkdown(text);
        })
        .catch((err) => {
          console.error('[FreeTierAgreementModal] failed to load:', err);
          this.terms_error =
            'Could not load the Free Tier Partner Agreement. ' +
            'Please refresh the page or contact support@kiey.com.';
        })
        .finally(() => {
          this.terms_loading = false;
        });
    },
    // Records acceptance against the global user doc and patches the
    // in-memory Authenticate.user so the parent's `should_show_*`
    // computed flips off and the modal disappears. The endpoint is
    // idempotent (`set({...}, { merge: true })`) so an accidental
    // double-submit is harmless.
    async acceptAgreement() {
      if (!this.can_submit) return;
      this.submit_error = '';
      this.submitting = true;
      try {
        await axios.put(
          config.api_url + 'users/me/free_terms_accept',
          { free_terms_version: config.free_terms_current_version }
        );
        // Patch the in-memory user so the parent layout's computed
        // immediately re-evaluates to false and unmounts us. updateUser
        // is a shallow merge — only the field we changed gets touched.
        this.$store.commit('Authenticate/updateUser', {
          free_terms_accepted_version: config.free_terms_current_version,
        });
      } catch (err) {
        console.error('[FreeTierAgreementModal] accept failed:', err);
        this.submit_error =
          "Couldn't record your acceptance. Please try again or contact support@kiey.com.";
      } finally {
        // Reset the spinner in all cases. On success the modal unmounts
        // (the parent's computed flips off) so this is moot; on failure
        // it re-enables the button so the user can retry without
        // reloading the page.
        this.submitting = false;
      }
    },
    // Cheap focus trap: if focus escapes the card (Tab past the last
    // focusable, Shift+Tab before the first), pull it back to the first
    // focusable element inside the card.
    installFocusTrap() {
      this.focusTrapHandler = (event) => {
        const card = this.$refs.card;
        if (!card) return;
        if (card.contains(event.target)) return;
        // Focus escaped — yank it back to the first focusable element.
        const focusable = card.querySelector(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && typeof focusable.focus === 'function') {
          event.stopPropagation();
          focusable.focus();
        }
      };
      document.addEventListener('focusin', this.focusTrapHandler, true);
    },
    // Swallow Escape — modal is intentionally non-dismissable. Tab
    // navigation continues to work because we do not call
    // preventDefault on it.
    installKeyHandler() {
      this.keyHandler = (event) => {
        if (event.key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          event.stopPropagation();
        }
      };
      document.addEventListener('keydown', this.keyHandler, true);
    },
  },
};
</script>

<style scoped>
.ftam-overlay {
  position: fixed;
  inset: 0;
  background: rgba(6, 29, 71, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 24px 16px;
  box-sizing: border-box;
}

.ftam-card {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(6, 29, 71, 0.28);
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  padding: 28px 28px 24px;
  box-sizing: border-box;
  outline: none;
}

.ftam-header {
  margin-bottom: 16px;
}

.ftam-heading {
  color: #061d47;
  font-family: 'Roboto-Medium', Arial, sans-serif;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.25;
}

.ftam-subhead {
  color: #4a5670;
  font-size: 14px;
  margin: 0;
  line-height: 1.5;
}

.ftam-terms-wrap {
  flex: 1 1 auto;
  min-height: 180px;
  margin-bottom: 16px;
  display: flex;
}

.ftam-terms {
  flex: 1 1 auto;
  border: 1px solid #e4e8f1;
  border-radius: 8px;
  background: #f9fafd;
  padding: 16px 18px;
  overflow-y: auto;
  max-height: 360px;
  font-size: 14px;
  color: #2d2d2d;
  line-height: 1.6;
  box-sizing: border-box;
}

.ftam-terms:focus {
  outline: 2px solid #29488d;
  outline-offset: 2px;
}

.ftam-terms-status {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6a7388;
  font-size: 14px;
  min-height: 180px;
}

.ftam-terms-error {
  color: #c0392b;
  text-align: center;
  padding: 16px;
}

.ftam-checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  cursor: pointer;
  user-select: none;
}

.ftam-checkbox-row input[type='checkbox'] {
  margin-top: 3px;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.ftam-checkbox-label {
  font-size: 14px;
  color: #2d2d2d;
  line-height: 1.5;
}

.ftam-submit-error {
  color: #c0392b;
  font-size: 13px;
  margin: 0 0 12px;
  line-height: 1.4;
}

.ftam-actions {
  display: flex;
  justify-content: flex-end;
}

.ftam-agree-btn {
  background: #061d47;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 12px 22px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, opacity 0.15s ease;
  min-width: 220px;
}

.ftam-agree-btn:hover:not(:disabled) {
  background: #0b2e6e;
}

.ftam-agree-btn:focus-visible {
  outline: 2px solid #29abe2;
  outline-offset: 2px;
}

.ftam-agree-btn:disabled {
  background: #98a4bf;
  cursor: not-allowed;
  opacity: 0.85;
}

/* Inline-rendered markdown content from renderLegalMarkdown. Mirrors
   the styles used in src/views/legal/*.vue so the agreement reads the
   same here as it does on the standalone legal pages. */
.ftam-terms >>> h2 {
  color: #061d47;
  font-size: 17px;
  font-weight: 700;
  margin: 18px 0 8px;
}
.ftam-terms >>> h3 {
  color: #061d47;
  font-size: 15px;
  font-weight: 700;
  margin: 14px 0 6px;
}
.ftam-terms >>> p { margin: 0 0 10px; }
.ftam-terms >>> ul,
.ftam-terms >>> ol { margin: 6px 0 12px; padding-left: 22px; }
.ftam-terms >>> li { margin-bottom: 4px; }
.ftam-terms >>> strong { color: #061d47; font-weight: 700; }
.ftam-terms >>> a { color: #29abe2; text-decoration: none; }
.ftam-terms >>> a:hover { text-decoration: underline; }
.ftam-terms >>> blockquote {
  margin: 10px 0;
  padding: 8px 12px;
  border-left: 3px solid #29488d;
  background: rgba(41, 73, 141, 0.05);
  border-radius: 0 4px 4px 0;
}
.ftam-terms >>> hr {
  border: 0;
  border-top: 1px solid #e4e8f1;
  margin: 18px 0;
}
.ftam-terms >>> code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 0.9em;
  font-family: 'SF Mono', Menlo, Monaco, Consolas, monospace;
}

@media (max-width: 600px) {
  .ftam-card {
    padding: 22px 18px 18px;
    border-radius: 10px;
  }
  .ftam-heading { font-size: 19px; }
  .ftam-terms { max-height: 280px; }
  .ftam-agree-btn {
    width: 100%;
    min-width: 0;
  }
  .ftam-actions { justify-content: stretch; }
}
</style>
